"use client";

import { authStore } from "@kaplan-labs/up-auth-api-client";
import { useStore } from "@nanostores/react";

import { deepEqual } from "./deep-equal";
import { persistentAtom } from "./persistent-store";
import { profileStore } from "./profile-context";
import { ProfileType } from "./types";

type OwnerStore = {
  email?: string;
  contactId?: string | null;
  firstName?: string;
  lastName?: string;
};

const OWNER_KEY = "__upowner";

export const ownerStore = persistentAtom<OwnerStore | undefined>(
  OWNER_KEY,
  undefined,
  {
    encode: JSON.stringify,
    decode: JSON.parse,
  },
);

const sync = () => {
  const $auth = authStore.get();
  const $profile = profileStore.get();
  const $owner = ownerStore.get();
  if (!$auth.currentUser) {
    ownerStore.set(undefined);
    return;
  }

  if ($profile.isLoading) {
    //covers edge case where owner is not set but profile does not change after loading
    if (!$owner?.contactId) {
      ownerStore.set({
        email: $auth.currentUser.email,
        contactId: $auth.currentUser.contactId,
      });
    }
    return;
  }

  if (!$profile.data) {
    ownerStore.set(undefined);
    return;
  }
  if (
    !("type" in $profile.data) ||
    $profile.data.type === ProfileType.Student
  ) {
    ownerStore.set({
      email: $auth.currentUser.email,
      contactId: $auth.currentUser.contactId,
    });
    return;
  }

  // Guardian with student(s), but Owner is currently set to Guardian
  const _owner = ownerStore.get();
  if (_owner?.email) {
    if (
      $profile.data.type === ProfileType.Guardian &&
      $profile.data.relatedStudents?.length &&
      $profile.data.relatedStudents[0] &&
      _owner.email === $auth.currentUser.email
    ) {
      ownerStore.set({
        email: $profile.data.relatedStudents[0].profile.email,
        firstName: $profile.data.relatedStudents[0].profile.firstName ?? "",
        lastName: $profile.data.relatedStudents[0].profile.lastName ?? "",
        contactId: $profile.data.relatedStudents[0]._id,
      });
      return;
    }
  }

  // Guardian with student(s), Owner is not currently set to Guardian
  if (_owner?.email) {
    if (
      $profile.data.type === ProfileType.Guardian &&
      $profile.data.relatedStudents?.length &&
      _owner.email !== $auth.currentUser.email
    ) {
      const students = $profile.data.relatedStudents;
      const student = students.find((s) => s.profile.email === _owner.email);

      if (student) {
        return;
      }

      ownerStore.set(undefined);
      return;
    }
  }

  if (
    $profile?.data?.type === ProfileType.Guardian &&
    $profile?.data?.relatedStudents?.length &&
    $profile.data.relatedStudents[0]
  ) {
    ownerStore.set({
      email: $profile.data.relatedStudents[0].profile.email,
      firstName: $profile.data.relatedStudents[0].profile.firstName ?? "",
      lastName: $profile.data.relatedStudents[0].profile.lastName ?? "",
      contactId: $profile.data.relatedStudents[0]._id,
    });
    return;
  }

  // Handle case where guardian has no students
  if (
    $profile.data.type === ProfileType.Guardian &&
    !$profile.data.relatedStudents?.length
  ) {
    ownerStore.set({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      email: $auth.currentUser!.email,
      contactId: $auth.currentUser.contactId,
    });
    return;
  }

  ownerStore.set(undefined);
  return ownerStore.get();
};

export const ownerInit = function () {
  profileStore.listen((newValue, oldValue) => {
    if (deepEqual(newValue?.contact, oldValue?.contact)) {
      return;
    }
    sync();
  });

  authStore.listen(sync);

  sync();
};

/*
  React hook to force renders when owner changes
*/
export const useOwner = function () {
  return useStore(ownerStore);
};
